import React, { useEffect, useMemo, useState } from "react";
import Logo from "../assets/images/logo.png";
import { initializeApp, deleteApp } from "firebase/app";
import { sendPasswordResetEmail, createUserWithEmailAndPassword, getAuth } from "firebase/auth";
import app, { auth } from "../lib/firebase";
import {
  listContent,
  createContent,
  updateContent,
  deleteContent,
  uploadImage,
  uploadMultipleImages,
  IMAGE_VALIDATION,
  validateImageFile,
  subscribeToRegistrations,
  updateRegistrationStatus,
  deleteRegistration,
  getSiteSettings,
  updateSiteSettings,
  seedExistingData,
  INITIAL_WEBSITE_DATA,
  listAdmins,
  addAdminUser,
  updateAdminRole,
  removeAdminUser,
} from "../lib/firestoreContent";
import CustomConfirmModal from "../components/CustomConfirmModal";

const CONTENT_SECTIONS = ["programs", "instructors", "team", "news", "announcements"];

const emptyData = {
  programs: [],
  instructors: [],
  team: [],
  news: [],
  announcements: [],
};

const IMAGE_FIELD_MAP = {
  programs: "imageUrl",
  instructors: "imageUrl",
  team: "imageUrl",
  news: "imageUrl",
  announcements: null,
};

const AdminDashboard = ({ onLogout, currentUser, userRole = "Admin" }) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [registrations, setRegistrations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [data, setData] = useState(emptyData);
  const [sectionState, setSectionState] = useState({
    programs: { loading: true, error: "" },
    instructors: { loading: true, error: "" },
    team: { loading: true, error: "" },
    news: { loading: true, error: "" },
    announcements: { loading: true, error: "" },
  });

  // Admins & Roles state
  const [adminsList, setAdminsList] = useState([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [adminsError, setAdminsError] = useState("");
  const [adminForm, setAdminForm] = useState({ email: "", name: "", role: "Admin" });
  const [adminFormStatus, setAdminFormStatus] = useState({ submitting: false, error: "", success: "" });

  // Registration modal state
  const [selectedRegistration, setSelectedRegistration] = useState(null);

  // Settings state
  const [settings, setSettings] = useState(INITIAL_WEBSITE_DATA.settings);
  const [settingsStatus, setSettingsStatus] = useState({ saving: false, error: "", success: "" });

  // Seeding status
  const [seedStatus, setSeedStatus] = useState({ seeding: false, message: "", error: "" });

  // Firestore connection notice
  const [dbNotice, setDbNotice] = useState("");

  // Custom Confirmation & Alert Modal State
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    title: "",
    message: "",
    confirmText: "Confirm",
    cancelText: "Cancel",
    type: "danger",
    onConfirm: null,
    showCancel: true,
  });

  const closeModal = () => {
    setModalConfig((prev) => ({ ...prev, isOpen: false }));
  };

  const showConfirm = ({
    title,
    message,
    confirmText = "Confirm",
    cancelText = "Cancel",
    type = "danger",
    onConfirm,
    showCancel = true,
  }) => {
    setModalConfig({
      isOpen: true,
      title,
      message,
      confirmText,
      cancelText,
      type,
      onConfirm: async () => {
        closeModal();
        if (onConfirm) await onConfirm();
      },
      showCancel,
    });
  };

  const [formStatus, setFormStatus] = useState({ submitting: false, error: "", success: "" });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [existingImageUrl, setExistingImageUrl] = useState("");
  // Gallery images state (for programs)
  const [galleryFiles, setGalleryFiles] = useState([]); // new File objects to upload
  const [galleryPreviews, setGalleryPreviews] = useState([]); // preview URLs for new files
  const [existingGalleryUrls, setExistingGalleryUrls] = useState([]); // already-uploaded URLs
  const [form, setForm] = useState({
    section: "programs",
    title: "",
    description: "",
    status: "Open",
    programType: "upcoming",
    eventDate: "",
    name: "",
    role: "",
    availability: "Available",
    summary: "",
    message: "",
    bio: "",
    ctaLabel: "Join Now!!!",
    ctaUrl: "/register",
    date: "",
    order: 1,
  });

  useEffect(() => {
    if (CONTENT_SECTIONS.includes(activeTab)) {
      setForm((current) => ({ ...current, section: activeTab }));
    }
  }, [activeTab]);

  // Load content from Firestore
  const loadSection = async (section) => {
    setSectionState((current) => ({ ...current, [section]: { loading: true, error: "" } }));
    try {
      const items = await listContent(section);
      setData((current) => ({ ...current, [section]: Array.isArray(items) ? items : [] }));
      setSectionState((current) => ({ ...current, [section]: { loading: false, error: "" } }));
    } catch (error) {
      console.error(`Error loading ${section}:`, error);
      if (error?.message?.includes("NOT_FOUND") || error?.code === "not-found") {
        setDbNotice(
          "Cloud Firestore is not yet created in your Firebase project (xplorestemi-4ce28). Please go to Firebase Console → Firestore Database → Create database."
        );
      }
      setData((current) => ({ ...current, [section]: Array.isArray(current[section]) ? current[section] : [] }));
      setSectionState((current) => ({
        ...current,
        [section]: { loading: false, error: error.message || "Unable to load content." },
      }));
    }
  };

  // Load site settings
  const loadSettings = async () => {
    try {
      const s = await getSiteSettings();
      if (s) setSettings(s);
    } catch (err) {
      console.warn("Error loading settings:", err);
    }
  };

  // Load administrators list
  const loadAdmins = async () => {
    setAdminsLoading(true);
    setAdminsError("");
    try {
      const list = await listAdmins();
      setAdminsList(Array.isArray(list) ? list : []);
    } catch (err) {
      console.warn("Error loading admins:", err);
      setAdminsError(err.message || "Failed to load administrators.");
    } finally {
      setAdminsLoading(false);
    }
  };

  // Creates a Firebase Auth account using a secondary app instance
  // so the current Super Admin's session is not affected.
  const createAuthAccount = async (email) => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%';
    const tempPassword = Array.from(
      crypto.getRandomValues(new Uint8Array(24)),
      (b) => chars[b % chars.length]
    ).join('');

    let secondaryApp;
    try {
      secondaryApp = initializeApp(app.options, `secondary-${Date.now()}`);
      const secondaryAuth = getAuth(secondaryApp);
      await createUserWithEmailAndPassword(secondaryAuth, email, tempPassword);
    } finally {
      if (secondaryApp) {
        await deleteApp(secondaryApp).catch(() => {});
      }
    }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    if (!adminForm.email) {
      setAdminFormStatus({ submitting: false, error: "Please provide an email address.", success: "" });
      return;
    }

    const normalizedEmail = adminForm.email.trim().toLowerCase();
    setAdminFormStatus({ submitting: true, error: "", success: "" });

    try {
      // Step 1: Create Firebase Auth account (skip if it already exists)
      try {
        await createAuthAccount(normalizedEmail);
      } catch (authErr) {
        if (authErr?.code !== "auth/email-already-in-use") {
          throw new Error(`Could not create authentication account: ${authErr.message || authErr}`);
        }
      }

      // Step 2: Create Firestore admin document
      await addAdminUser(adminForm, currentUser?.email || "Super Admin");

      // Step 3: Send password reset/setup email
      let emailSent = false;
      try {
        await sendPasswordResetEmail(auth, normalizedEmail);
        emailSent = true;
      } catch (emailErr) {
        console.warn("Could not send setup email:", emailErr);
      }

      const successMsg = emailSent
        ? `✅ Successfully added ${normalizedEmail} as ${adminForm.role}. A password setup email has been sent — they should check their inbox (and spam folder).`
        : `✅ Successfully added ${normalizedEmail} as ${adminForm.role}. Account created but the setup email could not be sent automatically. Use the "Send Setup Email" button in the admin list below.`;

      setAdminFormStatus({ submitting: false, error: "", success: successMsg });
      setAdminForm({ email: "", name: "", role: "Admin" });
      await loadAdmins();
    } catch (err) {
      setAdminFormStatus({ submitting: false, error: err.message || "Failed to add administrator.", success: "" });
    }
  };

  const handleSendSetupEmail = async (email) => {
    const normalizedEmail = email?.trim().toLowerCase();
    if (!normalizedEmail) return;

    try {
      // Try to create the auth account first if it doesn't exist
      try {
        await createAuthAccount(normalizedEmail);
      } catch (authErr) {
        if (authErr?.code !== "auth/email-already-in-use") {
          console.warn("Auth account creation skipped:", authErr?.code);
        }
      }

      await sendPasswordResetEmail(auth, normalizedEmail);
      showConfirm({
        title: "Email Sent",
        message: `A password setup/reset email has been sent to ${normalizedEmail}. They should check their inbox (and spam folder) to set their password.`,
        confirmText: "OK",
        type: "info",
        showCancel: false,
      });
    } catch (err) {
      console.error("Send setup email error:", err);
      showConfirm({
        title: "Could Not Send Email",
        message: err.message || "Failed to send setup email. Please try again.",
        confirmText: "Close",
        type: "danger",
        showCancel: false,
      });
    }
  };

  const handleToggleAdminRole = (admin) => {
    const targetRole = admin.role === "Super Admin" ? "Admin" : "Super Admin";
    const isPromoting = targetRole === "Super Admin";

    showConfirm({
      title: isPromoting ? "Promote to Super Admin" : "Demote to Standard Admin",
      message: isPromoting
        ? `Are you sure you want to promote ${admin.name || admin.email} to Super Admin? They will have full administrative rights, including managing other administrators and roles.`
        : `Are you sure you want to change ${admin.name || admin.email}'s role to Standard Admin?`,
      confirmText: isPromoting ? "Promote to Super Admin" : "Demote to Admin",
      cancelText: "Cancel",
      type: isPromoting ? "info" : "danger",
      onConfirm: async () => {
        try {
          await updateAdminRole(admin.email, targetRole, currentUser?.email || "Super Admin");
          await loadAdmins();
        } catch (err) {
          showConfirm({
            title: "Action Failed",
            message: err.message || "Could not update administrator role.",
            confirmText: "Close",
            type: "danger",
            showCancel: false,
          });
        }
      },
    });
  };

  const handleRemoveAdmin = (admin) => {
    showConfirm({
      title: "Revoke Administrator Access",
      message: `Are you sure you want to revoke admin access for ${admin.name || admin.email} (${admin.email})? They will immediately lose access to the admin console.`,
      confirmText: "Revoke Access",
      cancelText: "Cancel",
      type: "danger",
      onConfirm: async () => {
        try {
          await removeAdminUser(admin.email, currentUser?.email || "");
          await loadAdmins();
        } catch (err) {
          showConfirm({
            title: "Action Failed",
            message: err.message || "Could not remove administrator.",
            confirmText: "Close",
            type: "danger",
            showCancel: false,
          });
        }
      },
    });
  };

  useEffect(() => {
    CONTENT_SECTIONS.forEach((section) => loadSection(section));
    loadSettings();
    loadAdmins();

    // Real-time registrations listener
    const unsubscribe = subscribeToRegistrations((items) => {
      setRegistrations(items);
      setLoading(false);
    });

    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats = useMemo(() => {
    const regList = Array.isArray(registrations) ? registrations : [];
    const totalRegistrations = regList.length;
    const reviewedCount = regList.filter((r) => r.reviewStatus === "Reviewed").length;
    const programsList = Array.isArray(data?.programs) ? data.programs : [];
    const instructorsList = Array.isArray(data?.instructors) ? data.instructors : [];
    const teamList = Array.isArray(data?.team) ? data.team : [];

    return [
      { label: "Total Registrations", value: totalRegistrations.toString(), detail: `${reviewedCount} reviewed` },
      { label: "Active Programs", value: programsList.filter((item) => item?.status === "Open").length.toString(), detail: `${programsList.length} total catalog` },
      { label: "Instructors & Mentors", value: instructorsList.length.toString(), detail: "Active teachers" },
      { label: "Team Members", value: teamList.length.toString(), detail: "Core leadership & staff" },
    ];
  }, [registrations, data]);

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const resetForm = (section = form.section) => {
    setEditingId(null);
    setImageFile(null);
    setImagePreview("");
    setExistingImageUrl("");
    setGalleryFiles([]);
    setGalleryPreviews([]);
    setExistingGalleryUrls([]);
    setFormStatus({ submitting: false, error: "", success: "" });
    setForm({
      section,
      title: "",
      description: "",
      status: "Open",
      programType: "upcoming",
      eventDate: "",
      name: "",
      role: "",
      availability: "Available",
      summary: "",
      message: "",
      bio: "",
      ctaLabel: "Join Now!!!",
      ctaUrl: "/register",
      date: "",
      order: 1,
    });
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const err = validateImageFile(file);
    if (err) {
      setFormStatus({ submitting: false, error: err, success: "" });
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  // Gallery image handlers (for programs)
  const handleGalleryAdd = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const totalCount = existingGalleryUrls.length + galleryFiles.length + files.length;
    if (totalCount > IMAGE_VALIDATION.maxGalleryImages) {
      setFormStatus({
        submitting: false,
        error: `Maximum ${IMAGE_VALIDATION.maxGalleryImages} gallery images allowed. You have ${existingGalleryUrls.length + galleryFiles.length} already.`,
        success: "",
      });
      event.target.value = "";
      return;
    }

    for (const file of files) {
      const err = validateImageFile(file);
      if (err) {
        setFormStatus({ submitting: false, error: `"${file.name}": ${err}`, success: "" });
        event.target.value = "";
        return;
      }
    }

    setGalleryFiles((prev) => [...prev, ...files]);
    setGalleryPreviews((prev) => [...prev, ...files.map((f) => URL.createObjectURL(f))]);
    event.target.value = "";
  };

  const handleGalleryRemoveNew = (index) => {
    setGalleryFiles((prev) => prev.filter((_, i) => i !== index));
    setGalleryPreviews((prev) => {
      URL.revokeObjectURL(prev[index]);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleGalleryRemoveExisting = (index) => {
    setExistingGalleryUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddEntry = async (event) => {
    event.preventDefault();

    const section = form.section;

    if ((section === "instructors" || section === "team") && !form.name) return;
    if ((section === "programs" || section === "news" || section === "announcements") && !form.title) return;

    const itemPayload = (() => {
      switch (section) {
        case "programs":
          return {
            title: form.title,
            description: form.description || "No description yet.",
            status: form.status || "Open",
            programType: form.programType || "upcoming",
            eventDate: form.eventDate || "",
          };
        case "instructors":
          return {
            name: form.name,
            role: form.role || "Mentor",
            availability: form.availability || "Available",
            bio: form.bio || "",
          };
        case "team":
          return {
            name: form.name,
            role: form.role || "Team Member",
            bio: form.bio || "",
            order: Number(form.order) || 1,
          };
        case "news":
          return {
            title: form.title,
            summary: form.summary || "New update added.",
            date: form.date || "Recent Update",
          };
        case "announcements":
          return {
            title: form.title,
            message: form.message || "Announcement text",
            ctaLabel: form.ctaLabel || "Join Now!!!",
            ctaUrl: form.ctaUrl || "/register",
          };
        default:
          return null;
      }
    })();

    if (!itemPayload) return;

    setFormStatus({ submitting: true, error: "", success: "" });

    try {
      // Handle cover image upload to Cloudinary
      const imageField = IMAGE_FIELD_MAP[section];
      if (imageField && imageFile) {
        const url = await uploadImage(imageFile);
        itemPayload[imageField] = url;
      } else if (imageField && existingImageUrl) {
        itemPayload[imageField] = existingImageUrl;
      }

      // Handle gallery images for programs
      if (section === "programs") {
        let finalGalleryUrls = [...existingGalleryUrls];
        if (galleryFiles.length > 0) {
          const newUrls = await uploadMultipleImages(galleryFiles);
          finalGalleryUrls = [...finalGalleryUrls, ...newUrls];
        }
        itemPayload.galleryImages = finalGalleryUrls;
      }

      if (editingId !== null) {
        const updated = await updateContent(section, editingId, itemPayload);
        setData((current) => ({
          ...current,
          [section]: current[section].map((item) => (item.id === editingId ? updated : item)),
        }));
        setFormStatus({ submitting: false, error: "", success: "Entry updated successfully." });
      } else {
        const created = await createContent(section, itemPayload);
        setData((current) => ({
          ...current,
          [section]: [created, ...current[section]],
        }));
        setFormStatus({ submitting: false, error: "", success: "Entry published successfully." });
      }

      resetForm(section);
    } catch (error) {
      console.error(`Error saving ${section} entry:`, error);
      setFormStatus({ submitting: false, error: error.message || "Unable to save this entry.", success: "" });
    }
  };

  const handleEditEntry = (section, item) => {
    setEditingId(item.id);
    setImageFile(null);
    setImagePreview("");
    setExistingImageUrl(item.imageUrl || "");
    // Load gallery images if editing a program
    setGalleryFiles([]);
    setGalleryPreviews([]);
    setExistingGalleryUrls(Array.isArray(item.galleryImages) ? [...item.galleryImages] : []);
    setFormStatus({ submitting: false, error: "", success: "" });
    setForm({
      section,
      title: item.title || "",
      description: item.description || "",
      status: item.status || "Open",
      programType: item.programType || "upcoming",
      eventDate: item.eventDate || "",
      name: item.name || "",
      role: item.role || "",
      availability: item.availability || "Available",
      summary: item.summary || "",
      message: item.message || "",
      bio: item.bio || "",
      ctaLabel: item.ctaLabel || "Join Now!!!",
      ctaUrl: item.ctaUrl || "/register",
      date: item.date || "",
      order: item.order || 1,
    });
    setActiveTab(section);
  };

  const handleDelete = (section, id) => {
    const item = (data[section] || []).find((i) => i.id === id);
    const itemLabel = item?.title || item?.name || "this entry";
    const sectionName = section === "team" ? "team member" : section.slice(0, -1);

    showConfirm({
      title: `Delete ${sectionName}?`,
      message: `Are you sure you want to delete "${itemLabel}"? This action cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      type: "danger",
      onConfirm: async () => {
        const previous = Array.isArray(data[section]) ? data[section] : [];
        setData((current) => ({
          ...current,
          [section]: (Array.isArray(current[section]) ? current[section] : []).filter((i) => i?.id !== id),
        }));

        try {
          await deleteContent(section, id);
        } catch (error) {
          console.error(`Error deleting ${section} entry:`, error);
          setData((current) => ({ ...current, [section]: previous }));
          showConfirm({
            title: "Delete Failed",
            message: error.message || "Unable to delete this entry.",
            confirmText: "Dismiss",
            showCancel: false,
            type: "danger",
          });
        }
      },
    });
  };

  const handleToggleReview = async (id) => {
    const record = (Array.isArray(registrations) ? registrations : []).find((r) => r.id === id);
    if (!record) return;
    const newStatus = record.reviewStatus === "Reviewed" ? "Pending" : "Reviewed";

    // Optimistic update
    setRegistrations((current) =>
      (Array.isArray(current) ? current : []).map((item) =>
        item.id === id ? { ...item, reviewStatus: newStatus } : item
      )
    );

    if (selectedRegistration?.id === id) {
      setSelectedRegistration((prev) => ({ ...prev, reviewStatus: newStatus }));
    }

    try {
      await updateRegistrationStatus(id, newStatus);
    } catch (error) {
      console.error("Error updating review status:", error);
      setRegistrations((current) =>
        (Array.isArray(current) ? current : []).map((item) =>
          item.id === id ? { ...item, reviewStatus: record.reviewStatus } : item
        )
      );
    }
  };

  const handleDeleteRegistration = (id) => {
    const record = (Array.isArray(registrations) ? registrations : []).find((r) => r.id === id);
    const applicantName = record ? `${record.firstName} ${record.lastName}`.trim() : "this registration";

    showConfirm({
      title: "Delete Application?",
      message: `Are you sure you want to permanently delete the registration application for ${applicantName}? This action cannot be undone.`,
      confirmText: "Delete Application",
      cancelText: "Keep Application",
      type: "danger",
      onConfirm: async () => {
        const prev = registrations;
        setRegistrations((current) => current.filter((r) => r.id !== id));
        if (selectedRegistration?.id === id) {
          setSelectedRegistration(null);
        }

        try {
          await deleteRegistration(id);
        } catch (err) {
          console.error("Failed to delete registration:", err);
          setRegistrations(prev);
          showConfirm({
            title: "Error",
            message: "Failed to delete registration: " + (err.message || "Unknown error"),
            confirmText: "Close",
            showCancel: false,
            type: "danger",
          });
        }
      },
    });
  };

  const handleSaveSettings = async (e) => {
    if (e) e.preventDefault();
    setSettingsStatus({ saving: true, error: "", success: "" });
    try {
      await updateSiteSettings(settings);
      setSettingsStatus({ saving: false, error: "", success: "Settings saved successfully!" });
    } catch (err) {
      console.error("Error saving settings:", err);
      setSettingsStatus({ saving: false, error: err.message || "Failed to save settings.", success: "" });
    }
  };

  const handleToggleRegistration = async () => {
    const nextStatus = !settings.registrationOpen;
    setSettings((prev) => ({ ...prev, registrationOpen: nextStatus }));
    setSettingsStatus({ saving: true, error: "", success: "" });
    try {
      await updateSiteSettings({ ...settings, registrationOpen: nextStatus });
      setSettingsStatus({
        saving: false,
        error: "",
        success: `Registrations are now ${nextStatus ? "OPEN" : "CLOSED"} on the live website!`,
      });
    } catch (err) {
      console.error("Error toggling registration:", err);
      setSettings((prev) => ({ ...prev, registrationOpen: !nextStatus }));
      setSettingsStatus({
        saving: false,
        error: err.message || "Failed to toggle registration status.",
        success: "",
      });
    }
  };

  const handleSeedData = (force = false) => {
    showConfirm({
      title: "Sync & Restore Baseline Content?",
      message:
        "This will safely verify and synchronize default programs, instructors, team members, news items, and announcements in Firestore. Missing baseline content will be restored without duplicating existing entries. Proceed?",
      confirmText: "Sync Data Now",
      cancelText: "Cancel",
      type: "warning",
      onConfirm: async () => {
        setSeedStatus({ seeding: true, message: "Importing data into Firestore…", error: "" });

        try {
          const res = await seedExistingData(force);
          setSeedStatus({
            seeding: false,
            message: `Successfully populated: ${res.programs} programs, ${res.instructors} instructors, ${res.team} team members, ${res.news} news items, ${res.announcements} announcements.`,
            error: "",
          });
          // Refresh all content
          CONTENT_SECTIONS.forEach((s) => loadSection(s));
          loadSettings();
        } catch (err) {
          console.error("Seeding error:", err);
          setSeedStatus({
            seeding: false,
            message: "",
            error: err.message || "Seeding failed. Make sure Firestore database is created in Firebase Console.",
          });
        }
      },
    });
  };

  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "registrations", label: "Registrations" },
    { key: "programs", label: "Programs" },
    { key: "instructors", label: "Instructors" },
    { key: "team", label: "Team Members" },
    { key: "news", label: "News" },
    { key: "announcements", label: "Announcements" },
    { key: "settings", label: "Site Settings" },
    { key: "admins", label: "Admins & Roles" },
  ];

  const activeTabLabel = tabs.find((tab) => tab.key === activeTab)?.label || "Overview";

  const filteredRegistrations = (Array.isArray(registrations) ? registrations : []).filter((record) => {
    if (!record) return false;
    const haystack = [
      record.firstName,
      record.lastName,
      record.email,
      record.phone,
      record.preferredLearningTrack,
      record.reviewStatus,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return haystack.includes(searchTerm.toLowerCase());
  });

  const handleRefreshContent = () => {
    CONTENT_SECTIONS.forEach((section) => loadSection(section));
    loadSettings();
    loadAdmins();
  };

  const handleExportRegistrations = () => {
    const rows = [
      [
        "First Name",
        "Last Name",
        "Email",
        "Phone",
        "WhatsApp",
        "Track",
        "Experience",
        "Age",
        "Gender",
        "Availability",
        "Motivation",
        "Status",
      ],
      ...filteredRegistrations.map((record) => [
        record.firstName || "",
        record.lastName || "",
        record.email || "",
        record.phone || "",
        record.whatsappNumber || "",
        record.preferredLearningTrack || "",
        record.experienceLevel || "",
        record.age || "",
        record.gender || "",
        record.availability || "",
        record.motivationForJoining || "",
        record.reviewStatus || "Pending",
      ]),
    ];

    const csv = rows
      .map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "xplore-registrations.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="w-full">
        <div className="flex min-h-screen overflow-hidden bg-white">
          {/* Sidebar */}
          <aside className="hidden w-[280px] shrink-0 flex-col justify-between border-r border-white/10 bg-[#0f172a] p-5 lg:flex">
            <div>
              <div className="mb-8 rounded-xl bg-[#1e293b] p-3 ring-1 ring-inset ring-white/10">
                <div className="flex items-center gap-4">
                  <img src={Logo} alt="XploreStem logo" className="h-auto w-24 rounded-xl bg-white p-1.5 shadow-sm" />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.26em] text-[#94a3b8]">Admin</p>
                    <h2 className="text-lg font-semibold text-white">XploreStem</h2>
                  </div>
                </div>
              </div>

              <nav className="space-y-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => {
                      setActiveTab(tab.key);
                      if (CONTENT_SECTIONS.includes(tab.key)) resetForm(tab.key);
                      if (tab.key === "admins") loadAdmins();
                    }}
                    style={activeTab === tab.key ? undefined : { color: "#f8fafc" }}
                    className={`flex w-full items-center justify-between rounded-xl px-4 py-3 text-left text-sm font-medium transition ${
                      activeTab === tab.key
                        ? "bg-white text-[#0f172a] shadow-sm"
                        : "text-[#f8fafc] hover:bg-[#334155] hover:text-white"
                    }`}
                  >
                    <span>{tab.label}</span>
                    <span className={`h-2.5 w-2.5 rounded-full ${activeTab === tab.key ? "bg-primary" : "bg-[#94a3b8]"}`} />
                  </button>
                ))}
              </nav>
            </div>

            <div className="rounded-xl bg-[#1e293b] p-4 ring-1 ring-inset ring-white/10">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-emerald-400" />
                <p className="text-xs font-medium text-[#e2e8f0]">Console Online • Production</p>
              </div>

              {currentUser?.email && (
                <div className="mt-3 rounded-lg bg-[#0f172a]/70 p-2.5 border border-white/5">
                  <p className="text-[10px] uppercase tracking-wider text-[#94a3b8]">Signed in as</p>
                  <p className="truncate text-xs font-semibold text-white mt-0.5">{currentUser.email}</p>
                  <span
                    className={`mt-2 inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-bold tracking-wide ${
                      userRole === "Super Admin"
                        ? "bg-amber-500/20 text-amber-300 border border-amber-500/30"
                        : "bg-blue-500/20 text-blue-300 border border-blue-500/30"
                    }`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${userRole === "Super Admin" ? "bg-amber-400" : "bg-blue-400"}`} />
                    {userRole}
                  </span>
                </div>
              )}

              {onLogout && (
                <button
                  type="button"
                  onClick={onLogout}
                  className="mt-3 w-full rounded-xl border border-[#475569] px-4 py-2 text-sm font-medium text-[#f8fafc] transition hover:bg-[#334155]"
                >
                  Logout
                </button>
              )}
            </div>
          </aside>

          {/* Main Content Area */}
          <main className="flex-1 overflow-hidden bg-slate-50">
            <header className="bg-white/95 px-4 py-4 shadow-[0_1px_0_rgba(15,23,42,0.06)] backdrop-blur-sm sm:px-6">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.24em] text-slate-400">Operations / {activeTabLabel}</p>
                  <h1 className="text-2xl font-bold text-slate-900 md:text-3xl">{activeTab === "overview" ? "Admin dashboard" : activeTabLabel}</h1>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={handleRefreshContent}
                    className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                  >
                    Refresh content
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      resetForm("programs");
                      setActiveTab("programs");
                    }}
                    className="rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-white shadow-sm shadow-primary/20 transition hover:bg-[#1b1449]"
                  >
                    + Add new content
                  </button>
                </div>
              </div>
            </header>

            <div className="h-[calc(100vh-10rem)] overflow-y-auto p-4 sm:p-6">
              {/* Mobile tabs */}
              <div className="mb-6 flex flex-wrap gap-2 lg:hidden">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => {
                      setActiveTab(tab.key);
                      if (CONTENT_SECTIONS.includes(tab.key)) resetForm(tab.key);
                      if (tab.key === "admins") loadAdmins();
                    }}
                    className={`rounded-full px-3 py-2 text-xs font-semibold ${
                      activeTab === tab.key
                        ? "bg-primary text-white"
                        : "border border-slate-200 bg-white text-slate-700"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Database Notice Alert */}
              {dbNotice && (
                <div className="mb-6 rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-900">
                  <p className="font-semibold">⚠️ Cloud Firestore Notice</p>
                  <p className="mt-1 text-sm">{dbNotice}</p>
                  <a
                    href={`https://console.firebase.google.com/project/${process.env.REACT_APP_FIREBASE_PROJECT_ID}/firestore`}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-block text-xs font-bold text-amber-800 underline"
                  >
                    Open Firebase Console to Create Database →
                  </a>
                </div>
              )}

              {/* OVERVIEW TAB */}
              {activeTab === "overview" && (
                <>
                  <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    {stats.map((item, index) => (
                      <div key={item.label} className="rounded-xl bg-white p-5 shadow-[0_1px_3px_rgba(15,23,42,0.05),0_8px_24px_rgba(15,23,42,0.04)] ring-1 ring-slate-200/70">
                        <div className="mb-5 flex items-center justify-between">
                          <span className="text-sm text-slate-500">{item.label}</span>
                          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                            {index + 1}
                          </span>
                        </div>
                        <div className="text-3xl font-bold text-slate-900">{item.value}</div>
                        <p className="mt-2 text-sm text-slate-500">{item.detail}</p>
                      </div>
                    ))}
                  </div>

                  <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-5">
                    <div className="rounded-xl bg-white p-5 shadow-[0_1px_3px_rgba(15,23,42,0.05),0_8px_24px_rgba(15,23,42,0.04)] ring-1 ring-slate-200/70">
                      <p className="text-sm text-slate-500">Programs</p>
                      <h3 className="mt-3 text-2xl font-bold text-slate-900">{(data?.programs || []).length}</h3>
                      <p className="mt-2 text-sm text-slate-500">Training catalog entries</p>
                    </div>
                    <div className="rounded-xl bg-white p-5 shadow-[0_1px_3px_rgba(15,23,42,0.05),0_8px_24px_rgba(15,23,42,0.04)] ring-1 ring-slate-200/70">
                      <p className="text-sm text-slate-500">Instructors</p>
                      <h3 className="mt-3 text-2xl font-bold text-slate-900">{(data?.instructors || []).length}</h3>
                      <p className="mt-2 text-sm text-slate-500">Mentors & teachers</p>
                    </div>
                    <div className="rounded-xl bg-white p-5 shadow-[0_1px_3px_rgba(15,23,42,0.05),0_8px_24px_rgba(15,23,42,0.04)] ring-1 ring-slate-200/70">
                      <p className="text-sm text-slate-500">Team Members</p>
                      <h3 className="mt-3 text-2xl font-bold text-slate-900">{(data?.team || []).length}</h3>
                      <p className="mt-2 text-sm text-slate-500">About page team</p>
                    </div>
                    <div className="rounded-xl bg-white p-5 shadow-[0_1px_3px_rgba(15,23,42,0.05),0_8px_24px_rgba(15,23,42,0.04)] ring-1 ring-slate-200/70">
                      <p className="text-sm text-slate-500">News & Events</p>
                      <h3 className="mt-3 text-2xl font-bold text-slate-900">{(data?.news || []).length}</h3>
                      <p className="mt-2 text-sm text-slate-500">Articles & updates</p>
                    </div>
                    <div className="rounded-xl bg-white p-5 shadow-[0_1px_3px_rgba(15,23,42,0.05),0_8px_24px_rgba(15,23,42,0.04)] ring-1 ring-slate-200/70 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-slate-500">Registration Status</p>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold ${
                            settings?.registrationOpen ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
                          }`}>
                            {settings?.registrationOpen ? "LIVE / ACCEPTING" : "LOCKED"}
                          </span>
                        </div>
                        <h3 className={`mt-2 text-2xl font-bold ${settings?.registrationOpen ? "text-emerald-600" : "text-amber-600"}`}>
                          {settings?.registrationOpen ? "OPEN" : "CLOSED"}
                        </h3>
                      </div>
                      <div className="mt-3">
                        <button
                          type="button"
                          onClick={handleToggleRegistration}
                          disabled={settingsStatus.saving}
                          className={`w-full rounded-lg py-2 px-3 text-xs font-bold text-white transition shadow-sm ${
                            settings?.registrationOpen
                              ? "bg-amber-600 hover:bg-amber-700"
                              : "bg-emerald-600 hover:bg-emerald-700"
                          }`}
                        >
                          {settingsStatus.saving
                            ? "Updating live site…"
                            : settings?.registrationOpen
                            ? "✕ Click to Close Registration"
                            : "✓ Click to Open Registration"}
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* REGISTRATIONS TAB */}
              {activeTab === "registrations" && (
                <div className="rounded-xl bg-white shadow-[0_1px_3px_rgba(15,23,42,0.05),0_8px_24px_rgba(15,23,42,0.04)] ring-1 ring-slate-200/70">
                  <div className="sticky top-0 z-20 rounded-t-xl border-b border-slate-200 bg-white/95 p-4 backdrop-blur-sm sm:p-6">
                    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                      <div>
                        <h2 className="text-xl font-semibold text-slate-900">Registrations</h2>
                        <p className="text-sm text-slate-500">{(registrations || []).length} total applications submitted</p>
                      </div>
                      <div className="flex w-full flex-col gap-3 md:w-auto md:flex-row">
                        <input
                          type="text"
                          value={searchTerm}
                          onChange={(event) => setSearchTerm(event.target.value)}
                          placeholder="Search by name, email, track..."
                          className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700 outline-none ring-0 transition focus:border-slate-300 md:w-72"
                        />
                        <button
                          type="button"
                          onClick={handleExportRegistrations}
                          className="rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-white transition hover:bg-[#1b1449]"
                        >
                          Export CSV
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="max-h-[calc(100vh-17rem)] overflow-auto px-4 pb-4 sm:px-6 sm:pb-6">
                    <table className="min-w-full text-left">
                      <thead className="sticky top-0 z-10 bg-white">
                        <tr className="border-b border-slate-200 text-xs uppercase tracking-[0.12em] text-slate-500">
                          <th className="pb-3 pr-4 font-medium">Name</th>
                          <th className="pb-3 pr-4 font-medium">Track</th>
                          <th className="pb-3 pr-4 font-medium">Email</th>
                          <th className="pb-3 pr-4 font-medium">Phone</th>
                          <th className="pb-3 pr-4 font-medium">Review Status</th>
                          <th className="pb-3 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan="6" className="py-6 text-sm text-slate-500">Loading registrations…</td>
                          </tr>
                        ) : filteredRegistrations.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="py-6 text-sm text-slate-500">No registrations match your search.</td>
                          </tr>
                        ) : (
                          filteredRegistrations.map((record) => (
                            <tr key={record.id || `${record.email}-${record.phone}`} className="border-b border-slate-100 text-sm text-slate-700 hover:bg-slate-50">
                              <td className="py-3 pr-4 font-medium text-slate-900">{record.firstName} {record.lastName}</td>
                              <td className="py-3 pr-4">{record.preferredLearningTrack || "General"}</td>
                              <td className="py-3 pr-4">{record.email}</td>
                              <td className="py-3 pr-4">{record.phone}</td>
                              <td className="py-3 pr-4">
                                <button
                                  onClick={() => handleToggleReview(record.id)}
                                  className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${
                                    record.reviewStatus === "Reviewed"
                                      ? "bg-emerald-100 text-emerald-700"
                                      : "bg-amber-100 text-amber-700"
                                  }`}
                                >
                                  {record.reviewStatus === "Reviewed" ? "✓ Reviewed" : "● Pending"}
                                </button>
                              </td>
                              <td className="py-3 flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => setSelectedRegistration(record)}
                                  className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-200"
                                >
                                  View Details
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteRegistration(record.id)}
                                  className="rounded-lg bg-red-50 px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-100"
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* REGISTRATION DETAIL MODAL */}
              {selectedRegistration && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
                  <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                      <div>
                        <span className="text-xs font-bold uppercase tracking-wider text-primary">Student Application</span>
                        <h2 className="text-2xl font-bold text-slate-900">
                          {selectedRegistration.firstName} {selectedRegistration.lastName}
                        </h2>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedRegistration(null)}
                        className="rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                      >
                        ✕
                      </button>
                    </div>

                    <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 text-sm">
                      <div className="rounded-lg bg-slate-50 p-3">
                        <span className="text-xs text-slate-400">Email Address</span>
                        <p className="font-semibold text-slate-800">{selectedRegistration.email || "N/A"}</p>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-3">
                        <span className="text-xs text-slate-400">Phone Number</span>
                        <p className="font-semibold text-slate-800">{selectedRegistration.phone || "N/A"}</p>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-3">
                        <span className="text-xs text-slate-400">WhatsApp Number</span>
                        <p className="font-semibold text-slate-800">{selectedRegistration.whatsappNumber || "N/A"}</p>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-3">
                        <span className="text-xs text-slate-400">Learning Track</span>
                        <p className="font-semibold text-primary">{selectedRegistration.preferredLearningTrack || "General"}</p>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-3">
                        <span className="text-xs text-slate-400">Experience Level</span>
                        <p className="font-semibold text-slate-800">{selectedRegistration.experienceLevel || "N/A"}</p>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-3">
                        <span className="text-xs text-slate-400">Availability (8+ hrs/week)</span>
                        <p className="font-semibold text-slate-800">{selectedRegistration.availability || "N/A"}</p>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-3">
                        <span className="text-xs text-slate-400">Computer / Device</span>
                        <p className="font-semibold text-slate-800">{selectedRegistration.requirements || "N/A"}</p>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-3">
                        <span className="text-xs text-slate-400">Age & Gender</span>
                        <p className="font-semibold text-slate-800">
                          {selectedRegistration.age ? `${selectedRegistration.age} yrs` : "N/A"} • {selectedRegistration.gender || "N/A"}
                        </p>
                      </div>
                      {selectedRegistration.dateOfBirth && (
                        <div className="rounded-lg bg-slate-50 p-3">
                          <span className="text-xs text-slate-400">Date of Birth</span>
                          <p className="font-semibold text-slate-800">{selectedRegistration.dateOfBirth}</p>
                        </div>
                      )}
                      <div className="rounded-lg bg-slate-50 p-3">
                        <span className="text-xs text-slate-400">Review Status</span>
                        <p className="mt-1">
                          <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-bold ${
                            selectedRegistration.reviewStatus === "Reviewed"
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-amber-100 text-amber-700"
                          }`}>
                            {selectedRegistration.reviewStatus || "Pending"}
                          </span>
                        </p>
                      </div>
                    </div>

                    {selectedRegistration.motivationForJoining && (
                      <div className="mt-4 rounded-lg bg-slate-50 p-4">
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Motivation for Joining</span>
                        <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{selectedRegistration.motivationForJoining}</p>
                      </div>
                    )}

                    {selectedRegistration.additionalInformation && (
                      <div className="mt-4 rounded-lg bg-slate-50 p-4">
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Additional Information / Questions</span>
                        <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{selectedRegistration.additionalInformation}</p>
                      </div>
                    )}

                    <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-4">
                      <button
                        type="button"
                        onClick={() => handleToggleReview(selectedRegistration.id)}
                        className={`rounded-xl px-4 py-2 text-sm font-semibold ${
                          selectedRegistration.reviewStatus === "Reviewed"
                            ? "border border-amber-300 bg-amber-50 text-amber-700"
                            : "bg-emerald-600 text-white hover:bg-emerald-700"
                        }`}
                      >
                        {selectedRegistration.reviewStatus === "Reviewed" ? "Mark as Pending" : "Mark as Reviewed"}
                      </button>

                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => handleDeleteRegistration(selectedRegistration.id)}
                          className="rounded-xl bg-red-50 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-100"
                        >
                          Delete Application
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedRegistration(null)}
                          className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* SITE SETTINGS TAB */}
              {activeTab === "settings" && (
                <div className="max-w-4xl space-y-6">
                  <div className="rounded-xl bg-white p-6 shadow-[0_1px_3px_rgba(15,23,42,0.05),0_8px_24px_rgba(15,23,42,0.04)] ring-1 ring-slate-200/70">
                    <h2 className="text-xl font-bold text-slate-900">Site Configuration & Controls</h2>
                    <p className="mt-1 text-sm text-slate-500">
                      Manage global website behavior, active registration toggles, and contact channels.
                    </p>

                    <form onSubmit={handleSaveSettings} className="mt-6 space-y-6">
                    {/* Registration Toggle Card */}
                    <div className={`rounded-2xl border p-5 transition ${
                      settings.registrationOpen
                        ? "border-emerald-200 bg-emerald-50/50"
                        : "border-amber-200 bg-amber-50/50"
                    }`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-slate-900">Registration Status</h3>
                          <p className="mt-1 text-xs text-slate-600">
                            {settings.registrationOpen
                              ? "Registrations are currently OPEN. Students can fill out the registration form at /register."
                              : "Registrations are currently CLOSED. The registration page displays 'Registration Closed'."}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <button
                            type="button"
                            onClick={handleToggleRegistration}
                            disabled={settingsStatus.saving}
                            className={`rounded-full px-5 py-2.5 text-sm font-bold text-white transition shadow-sm ${
                              settings.registrationOpen ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-700 hover:bg-slate-800"
                            }`}
                          >
                            {settingsStatus.saving
                              ? "Saving live…"
                              : settings.registrationOpen
                              ? "✓ OPEN (Click to Close)"
                              : "✕ CLOSED (Click to Open)"}
                          </button>
                          {settingsStatus.success && (
                            <span className="text-xs text-emerald-600 font-semibold">{settingsStatus.success}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Contact details */}
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600">Contact Email</label>
                        <input
                          type="email"
                          value={settings.contactEmail || ""}
                          onChange={(e) => setSettings((prev) => ({ ...prev, contactEmail: e.target.value }))}
                          className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600">Contact Phone</label>
                        <input
                          type="text"
                          value={settings.contactPhone || ""}
                          onChange={(e) => setSettings((prev) => ({ ...prev, contactPhone: e.target.value }))}
                          className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600">Instagram Handle</label>
                        <input
                          type="text"
                          value={settings.instagramHandle || ""}
                          onChange={(e) => setSettings((prev) => ({ ...prev, instagramHandle: e.target.value }))}
                          className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600">Office Hours</label>
                        <input
                          type="text"
                          value={settings.officeHours || ""}
                          onChange={(e) => setSettings((prev) => ({ ...prev, officeHours: e.target.value }))}
                          className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600">WhatsApp Community Group Link</label>
                        <input
                          type="url"
                          placeholder="https://chat.whatsapp.com/..."
                          value={settings.whatsappCommunityUrl || ""}
                          onChange={(e) => setSettings((prev) => ({ ...prev, whatsappCommunityUrl: e.target.value }))}
                          className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800"
                        />
                      </div>
                    </div>

                    {/* Mission and Vision */}
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600">Mission Statement</label>
                      <textarea
                        rows="3"
                        value={settings.missionText || ""}
                        onChange={(e) => setSettings((prev) => ({ ...prev, missionText: e.target.value }))}
                        className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600">Vision Statement</label>
                      <textarea
                        rows="3"
                        value={settings.visionText || ""}
                        onChange={(e) => setSettings((prev) => ({ ...prev, visionText: e.target.value }))}
                        className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800"
                      />
                    </div>

                    {settingsStatus.error && (
                      <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{settingsStatus.error}</p>
                    )}
                    {settingsStatus.success && (
                      <p className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-700">{settingsStatus.success}</p>
                    )}

                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={settingsStatus.saving}
                        className="rounded-xl bg-primary px-6 py-3 font-semibold text-white transition hover:bg-[#1b1449] disabled:opacity-50"
                      >
                        {settingsStatus.saving ? "Saving Changes…" : "Save Site Settings"}
                      </button>
                    </div>
                  </form>
                </div>

                {/* Data Maintenance & Baseline Sync Card */}
                <div className="rounded-xl bg-white p-6 shadow-[0_1px_3px_rgba(15,23,42,0.05),0_8px_24px_rgba(15,23,42,0.04)] ring-1 ring-slate-200/70">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-bold text-slate-900">Data Maintenance & Baseline Sync</h3>
                        <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600">
                          Maintenance
                        </span>
                      </div>
                      <p className="mt-1 max-w-2xl text-sm text-slate-500">
                        Verify and synchronize default baseline website content (programs, mentors, team members, news, announcements) in Firestore. This safely restores missing baseline entries and assets without duplicating existing data.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleSeedData(true)}
                      disabled={seedStatus.seeding}
                      className="rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-50 whitespace-nowrap"
                    >
                      {seedStatus.seeding ? "Syncing Data…" : "Sync / Restore Baseline Data"}
                    </button>
                  </div>

                  {seedStatus.message && (
                    <p className="mt-4 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-700 ring-1 ring-emerald-200">
                      ✓ {seedStatus.message}
                    </p>
                  )}
                  {seedStatus.error && (
                    <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700 ring-1 ring-red-200">
                      ✕ {seedStatus.error}
                    </p>
                  )}
                </div>
              </div>
            )}

              {/* ADMINS & ROLES TAB */}
              {activeTab === "admins" && (
                <div className="space-y-6">
                  {/* Page Header Banner */}
                  <div className="rounded-xl bg-white p-6 shadow-[0_1px_3px_rgba(15,23,42,0.05),0_8px_24px_rgba(15,23,42,0.04)] ring-1 ring-slate-200/70">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <h2 className="text-xl font-bold text-slate-900">Administrator Access & Roles</h2>
                        <p className="mt-1 text-sm text-slate-500">
                          Control console access, invite authorized administrators, and delegate Super Admin privileges.
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
                            userRole === "Super Admin"
                              ? "bg-amber-100 text-amber-900 border border-amber-300"
                              : "bg-blue-100 text-blue-800 border border-blue-200"
                          }`}
                        >
                          <span
                            className={`h-1.5 w-1.5 rounded-full ${
                              userRole === "Super Admin" ? "bg-amber-500" : "bg-blue-500"
                            }`}
                          />
                          Your Role: {userRole}
                        </span>
                      </div>
                    </div>

                    {/* Overview Stat Badges */}
                    <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                      <div className="rounded-xl border border-slate-200/60 bg-slate-50 p-4">
                        <p className="text-xs font-medium text-slate-500">Total Administrators</p>
                        <p className="mt-1 text-2xl font-bold text-slate-900">{adminsList.length}</p>
                      </div>
                      <div className="rounded-xl border border-amber-200/60 bg-amber-50/60 p-4">
                        <p className="text-xs font-medium text-amber-700">Super Admins</p>
                        <p className="mt-1 text-2xl font-bold text-amber-900">
                          {adminsList.filter((a) => a.role === "Super Admin").length}
                        </p>
                      </div>
                      <div className="rounded-xl border border-blue-200/60 bg-blue-50/60 p-4">
                        <p className="text-xs font-medium text-blue-700">Standard Admins</p>
                        <p className="mt-1 text-2xl font-bold text-blue-900">
                          {adminsList.filter((a) => a.role !== "Super Admin").length}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Add Administrator Form (Only accessible to Super Admin) */}
                  {userRole === "Super Admin" ? (
                    <div className="rounded-xl bg-white p-6 shadow-[0_1px_3px_rgba(15,23,42,0.05),0_8px_24px_rgba(15,23,42,0.04)] ring-1 ring-slate-200/70">
                      <h3 className="text-lg font-bold text-slate-900">Add New Administrator</h3>
                      <p className="mt-1 text-xs text-slate-500">
                        Authorize a team member by email. You can grant them standard Admin rights or make them a Super Admin directly.
                      </p>

                      <form onSubmit={handleAddAdmin} className="mt-5 space-y-4">
                        <div className="grid gap-4 md:grid-cols-3">
                          <div>
                            <label className="block text-xs font-medium text-slate-700">Email Address *</label>
                            <input
                              type="email"
                              required
                              value={adminForm.email}
                              onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                              placeholder="colleague@xplorestemi.org"
                              className="mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-slate-700">Full Name</label>
                            <input
                              type="text"
                              value={adminForm.name}
                              onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })}
                              placeholder="e.g. Alex Johnson"
                              className="mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-slate-700">Assign Role *</label>
                            <select
                              value={adminForm.role}
                              onChange={(e) => setAdminForm({ ...adminForm, role: e.target.value })}
                              className="mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                            >
                              <option value="Admin">Admin (Can edit content, registrations & settings)</option>
                              <option value="Super Admin">Super Admin (Full access + manage admins & roles)</option>
                            </select>
                          </div>
                        </div>

                        {adminFormStatus.error && (
                          <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs font-medium text-red-700">
                            {adminFormStatus.error}
                          </p>
                        )}
                        {adminFormStatus.success && (
                          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                            <p className="text-xs font-medium text-emerald-700">
                              {adminFormStatus.success}
                            </p>
                            <button
                              type="button"
                              onClick={() => {
                                const emailMatch = adminFormStatus.success.match(/added\s+(\S+@\S+)\s+as/);
                                if (emailMatch?.[1]) handleSendSetupEmail(emailMatch[1]);
                              }}
                              className="mt-2 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100"
                            >
                              ✉ Send Password Setup Email Now
                            </button>
                          </div>
                        )}

                        <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between">
                          <p className="text-[11px] text-slate-500">
                            <strong>Step 1:</strong> Add admin here. <strong>Step 2:</strong> Ensure the user has a Firebase Auth account (create one in the Firebase Console if needed). <strong>Step 3:</strong> Click "Send Setup Email" so they can set their password.
                          </p>
                          <button
                            type="submit"
                            disabled={adminFormStatus.submitting}
                            className="rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1b1449] disabled:opacity-50"
                          >
                            {adminFormStatus.submitting ? "Adding…" : "+ Grant Admin Access"}
                          </button>
                        </div>
                      </form>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-4">
                      <p className="text-xs text-blue-800">
                        <strong className="font-semibold">Note:</strong> You are signed in as a Standard Admin. You have full access to manage website content, registrations, and site configuration. Only Super Admins can add new administrators or alter administrative roles.
                      </p>
                    </div>
                  )}

                  {/* Administrators Directory List */}
                  <div className="rounded-xl bg-white p-6 shadow-[0_1px_3px_rgba(15,23,42,0.05),0_8px_24px_rgba(15,23,42,0.04)] ring-1 ring-slate-200/70">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                      <h3 className="text-lg font-bold text-slate-900">Current Administrators</h3>
                      <button
                        type="button"
                        onClick={loadAdmins}
                        disabled={adminsLoading}
                        className="text-xs font-medium text-primary hover:underline"
                      >
                        {adminsLoading ? "Refreshing…" : "Refresh List"}
                      </button>
                    </div>

                    {adminsError && (
                      <p className="mt-4 rounded-lg bg-red-50 p-3 text-xs text-red-700">{adminsError}</p>
                    )}

                    {adminsLoading && adminsList.length === 0 ? (
                      <div className="py-12 text-center text-sm text-slate-400">Loading administrators…</div>
                    ) : adminsList.length === 0 ? (
                      <div className="py-12 text-center text-sm text-slate-400">
                        No administrators found. The first logged-in user becomes Super Admin automatically.
                      </div>
                    ) : (
                      <div className="mt-4 divide-y divide-slate-100">
                        {adminsList.map((admin) => {
                          const isCurrentUser = admin.email?.toLowerCase() === currentUser?.email?.toLowerCase();
                          const isSuperAdmin = admin.role === "Super Admin";
                          const superAdminCount = adminsList.filter((a) => a.role === "Super Admin").length;
                          const isLastSuperAdmin = isSuperAdmin && superAdminCount <= 1;

                          return (
                            <div
                              key={admin.id || admin.email}
                              className="flex flex-col gap-4 py-4 sm:flex-row sm:items-center sm:justify-between"
                            >
                              {/* Admin info & avatar */}
                              <div className="flex items-center gap-3.5">
                                <div
                                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white shadow-sm ${
                                    isSuperAdmin
                                      ? "bg-gradient-to-tr from-amber-500 to-amber-600 ring-2 ring-amber-200"
                                      : "bg-gradient-to-tr from-slate-600 to-slate-800"
                                  }`}
                                >
                                  {(admin.name || admin.email || "A").charAt(0).toUpperCase()}
                                </div>

                                <div>
                                  <div className="flex items-center gap-2">
                                    <p className="font-semibold text-slate-900">
                                      {admin.name || admin.email.split("@")[0]}
                                    </p>
                                    {isCurrentUser && (
                                      <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-600">
                                        You
                                      </span>
                                    )}
                                    <span
                                      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
                                        isSuperAdmin
                                          ? "border border-amber-200 bg-amber-100 text-amber-800"
                                          : "border border-slate-200 bg-slate-100 text-slate-700"
                                      }`}
                                    >
                                      {isSuperAdmin ? "★ Super Admin" : "Admin"}
                                    </span>
                                  </div>

                                  <p className="text-xs text-slate-500">{admin.email}</p>
                                  <p className="mt-0.5 text-[10px] text-slate-400">
                                    Added {admin.addedBy ? `by ${admin.addedBy}` : "via system setup"}
                                  </p>
                                </div>
                              </div>

                              {/* Actions */}
                              {userRole === "Super Admin" && (
                                <div className="flex flex-wrap items-center gap-2 self-end sm:self-center">
                                  {/* Make Super Admin / Demote to Admin */}
                                  {isSuperAdmin ? (
                                    <button
                                      type="button"
                                      onClick={() => handleToggleAdminRole(admin)}
                                      disabled={isLastSuperAdmin}
                                      title={
                                        isLastSuperAdmin
                                          ? "Cannot demote the only Super Admin"
                                          : "Change to standard Admin"
                                      }
                                      className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                                    >
                                      Demote to Admin
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => handleToggleAdminRole(admin)}
                                      className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-900 transition hover:bg-amber-100"
                                    >
                                      ★ Make Super Admin
                                    </button>
                                  )}

                                  {/* Send Password Setup Email */}
                                  <button
                                    type="button"
                                    onClick={() => handleSendSetupEmail(admin.email)}
                                    title="Send password setup/reset email"
                                    className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 transition hover:bg-blue-100"
                                  >
                                    ✉ Send Setup Email
                                  </button>

                                  {/* Revoke Access */}
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveAdmin(admin)}
                                    disabled={isLastSuperAdmin || isCurrentUser}
                                    title={
                                      isCurrentUser
                                        ? "You cannot revoke your own account"
                                        : isLastSuperAdmin
                                        ? "Cannot delete the only Super Admin"
                                        : "Revoke administrator access"
                                    }
                                    className="rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-40"
                                  >
                                    Revoke Access
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* CONTENT CRUD TABS (Programs, Instructors, Team, News, Announcements) */}
              {CONTENT_SECTIONS.includes(activeTab) && (
                <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
                  {/* Form Component */}
                  <form onSubmit={handleAddEntry} className="rounded-xl bg-white p-5 shadow-[0_1px_3px_rgba(15,23,42,0.05),0_8px_24px_rgba(15,23,42,0.04)] ring-1 ring-slate-200/70 sm:p-6">
                    <div className="mb-5 flex items-center justify-between">
                      <h2 className="text-xl font-semibold text-slate-900">
                        {editingId !== null ? "Edit" : "Add"} {activeTab === "team" ? "Team Member" : activeTab.slice(0, -1)}
                      </h2>
                      {editingId !== null && (
                        <button type="button" onClick={() => resetForm(activeTab)} className="text-sm font-medium text-slate-500">
                          Cancel
                        </button>
                      )}
                    </div>

                    <div className="space-y-4">
                      {/* PROGRAMS FORM */}
                      {activeTab === "programs" && (
                        <>
                          {/* Program Type Selector */}
                          <div>
                            <label className="mb-1.5 block text-xs font-medium uppercase tracking-[0.1em] text-slate-500">
                              Program Type *
                            </label>
                            <select
                              value={form.programType}
                              onChange={(event) => updateForm("programType", event.target.value)}
                              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700"
                            >
                              <option value="upcoming">Upcoming / Current Program</option>
                              <option value="completed">Completed Training (Historical Record)</option>
                            </select>
                            <p className="mt-1 text-[11px] text-slate-400">
                              {form.programType === "completed"
                                ? "This will appear under \"A Record of Our Training\" / Gallery section."
                                : "This will appear under \"Our Programs\" on the training page."}
                            </p>
                          </div>

                          <input
                            value={form.title}
                            onChange={(event) => updateForm("title", event.target.value)}
                            placeholder="Program title (e.g. Online Robotics Bootcamp)"
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700"
                            required
                          />
                          <textarea
                            value={form.description}
                            onChange={(event) => updateForm("description", event.target.value)}
                            placeholder={"Program description — use multiple lines for paragraphs.\n\nLine breaks will be preserved on the website."}
                            rows="5"
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700"
                            required
                          />

                          {/* Event Date (especially useful for completed trainings) */}
                          <div>
                            <label className="mb-1.5 block text-xs font-medium uppercase tracking-[0.1em] text-slate-500">
                              Event Date {form.programType === "completed" ? "*" : "(optional)"}
                            </label>
                            <input
                              type="date"
                              value={form.eventDate}
                              onChange={(event) => updateForm("eventDate", event.target.value)}
                              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700"
                            />
                          </div>

                          <select
                            value={form.status}
                            onChange={(event) => updateForm("status", event.target.value)}
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700"
                          >
                            <option value="Open">Open</option>
                            <option value="Closed">Closed</option>
                            <option value="Completed">Completed</option>
                            <option value="Draft">Draft</option>
                          </select>
                        </>
                      )}

                      {/* INSTRUCTORS FORM */}
                      {activeTab === "instructors" && (
                        <>
                          <input
                            value={form.name}
                            onChange={(event) => updateForm("name", event.target.value)}
                            placeholder="Instructor name"
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700"
                            required
                          />
                          <input
                            value={form.role}
                            onChange={(event) => updateForm("role", event.target.value)}
                            placeholder="Role (e.g. Founder, Mentor)"
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700"
                          />
                          <textarea
                            value={form.bio}
                            onChange={(event) => updateForm("bio", event.target.value)}
                            placeholder="Bio or educational background"
                            rows="3"
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700"
                          />
                          <select
                            value={form.availability}
                            onChange={(event) => updateForm("availability", event.target.value)}
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700"
                          >
                            <option value="Available">Available</option>
                            <option value="Booked">Booked</option>
                          </select>
                        </>
                      )}

                      {/* TEAM FORM */}
                      {activeTab === "team" && (
                        <>
                          <input
                            value={form.name}
                            onChange={(event) => updateForm("name", event.target.value)}
                            placeholder="Team member name"
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700"
                            required
                          />
                          <input
                            value={form.role}
                            onChange={(event) => updateForm("role", event.target.value)}
                            placeholder="Role (e.g. Operations Director, Developer)"
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700"
                            required
                          />
                          <textarea
                            value={form.bio}
                            onChange={(event) => updateForm("bio", event.target.value)}
                            placeholder="Bio or specialization (optional)"
                            rows="3"
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700"
                          />
                          <input
                            type="number"
                            value={form.order}
                            onChange={(event) => updateForm("order", event.target.value)}
                            placeholder="Display Order (1, 2, 3...)"
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700"
                          />
                        </>
                      )}

                      {/* NEWS FORM */}
                      {activeTab === "news" && (
                        <>
                          <input
                            value={form.title}
                            onChange={(event) => updateForm("title", event.target.value)}
                            placeholder="News article or partnership title"
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700"
                            required
                          />
                          <textarea
                            value={form.summary}
                            onChange={(event) => updateForm("summary", event.target.value)}
                            placeholder="News summary or details"
                            rows="4"
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700"
                            required
                          />
                          <input
                            value={form.date}
                            onChange={(event) => updateForm("date", event.target.value)}
                            placeholder="Date or label (e.g. 16th September 2021)"
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700"
                          />
                        </>
                      )}

                      {/* ANNOUNCEMENTS FORM */}
                      {activeTab === "announcements" && (
                        <>
                          <input
                            value={form.title}
                            onChange={(event) => updateForm("title", event.target.value)}
                            placeholder="Announcement title"
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700"
                            required
                          />
                          <textarea
                            value={form.message}
                            onChange={(event) => updateForm("message", event.target.value)}
                            placeholder="Announcement message text"
                            rows="4"
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700"
                            required
                          />
                          <div className="grid grid-cols-2 gap-3">
                            <input
                              value={form.ctaLabel}
                              onChange={(event) => updateForm("ctaLabel", event.target.value)}
                              placeholder="CTA Button text (e.g. Join Now!!!)"
                              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700"
                            />
                            <input
                              value={form.ctaUrl}
                              onChange={(event) => updateForm("ctaUrl", event.target.value)}
                              placeholder="CTA URL (e.g. /register)"
                              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700"
                            />
                          </div>
                        </>
                      )}

                      {/* IMAGE UPLOAD (Cloudinary) */}
                      {IMAGE_FIELD_MAP[activeTab] && (
                        <div>
                          <label className="mb-1.5 block text-xs font-medium uppercase tracking-[0.1em] text-slate-500">
                            {activeTab === "instructors" || activeTab === "team" ? "Profile Photo" : "Cover Image"}
                          </label>
                          <input
                            type="file"
                            accept={IMAGE_VALIDATION.acceptString}
                            onChange={handleImageChange}
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700"
                          />
                          {(imagePreview || existingImageUrl) && (
                            <div className="mt-3 flex items-center gap-3">
                              <img
                                src={imagePreview || existingImageUrl}
                                alt="Preview"
                                className="h-20 w-20 rounded-lg object-cover ring-1 ring-slate-200"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  setImageFile(null);
                                  setImagePreview("");
                                  setExistingImageUrl("");
                                }}
                                className="text-xs text-red-500 hover:underline"
                              >
                                Remove Image
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      {/* GALLERY IMAGES (Programs only) */}
                      {activeTab === "programs" && (
                        <div>
                          <label className="mb-1.5 block text-xs font-medium uppercase tracking-[0.1em] text-slate-500">
                            Gallery / Activity Pictures
                          </label>
                          <p className="mb-2 text-[11px] text-slate-400">
                            Upload additional photos from this training event (max {IMAGE_VALIDATION.maxGalleryImages} images, {IMAGE_VALIDATION.maxSizeMB}MB each). These appear alongside the cover image on the website.
                          </p>
                          <input
                            type="file"
                            accept={IMAGE_VALIDATION.acceptString}
                            multiple
                            onChange={handleGalleryAdd}
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700"
                          />
                          {/* Gallery Preview Grid */}
                          {(existingGalleryUrls.length > 0 || galleryPreviews.length > 0) && (
                            <div className="mt-3">
                              <p className="mb-2 text-[11px] font-semibold text-slate-500">
                                {existingGalleryUrls.length + galleryPreviews.length} gallery image(s)
                              </p>
                              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                                {existingGalleryUrls.map((url, idx) => (
                                  <div key={`existing-${idx}`} className="group relative">
                                    <img
                                      src={url}
                                      alt={`Gallery ${idx + 1}`}
                                      className="h-20 w-full rounded-lg object-cover ring-1 ring-slate-200"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => handleGalleryRemoveExisting(idx)}
                                      className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white opacity-0 shadow transition group-hover:opacity-100"
                                      title="Remove this image"
                                    >
                                      ✕
                                    </button>
                                    <span className="absolute bottom-0.5 left-0.5 rounded bg-black/50 px-1 py-0.5 text-[8px] text-white">
                                      Saved
                                    </span>
                                  </div>
                                ))}
                                {galleryPreviews.map((previewUrl, idx) => (
                                  <div key={`new-${idx}`} className="group relative">
                                    <img
                                      src={previewUrl}
                                      alt={`New ${idx + 1}`}
                                      className="h-20 w-full rounded-lg object-cover ring-1 ring-blue-200"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => handleGalleryRemoveNew(idx)}
                                      className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white opacity-0 shadow transition group-hover:opacity-100"
                                      title="Remove this image"
                                    >
                                      ✕
                                    </button>
                                    <span className="absolute bottom-0.5 left-0.5 rounded bg-blue-600/70 px-1 py-0.5 text-[8px] text-white">
                                      New
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {formStatus.error && (
                        <p role="alert" className="rounded-lg bg-red-50 px-3 py-2.5 text-sm text-red-700">{formStatus.error}</p>
                      )}
                      {formStatus.success && (
                        <p role="status" className="rounded-lg bg-emerald-50 px-3 py-2.5 text-sm text-emerald-700">{formStatus.success}</p>
                      )}

                      <button
                        type="submit"
                        disabled={formStatus.submitting}
                        className="w-full rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#1b1449] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {formStatus.submitting
                          ? (galleryFiles.length > 0 ? "Uploading images & saving…" : "Saving…")
                          : editingId !== null
                          ? "Update entry"
                          : "Save entry"}
                      </button>
                    </div>
                  </form>

                  {/* List / Management Component */}
                  <div className="rounded-xl bg-white p-5 shadow-[0_1px_3px_rgba(15,23,42,0.05),0_8px_24px_rgba(15,23,42,0.04)] ring-1 ring-slate-200/70 sm:p-6">
                    <div className="mb-5 flex items-center justify-between">
                      <h2 className="text-xl font-semibold text-slate-900">
                        Existing {activeTab === "team" ? "Team Members" : activeTab}
                      </h2>
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                        {(data[activeTab] || []).length} items
                      </span>
                    </div>

                    {sectionState[activeTab]?.loading ? (
                      <p className="text-sm text-slate-500">Loading {activeTab}…</p>
                    ) : sectionState[activeTab]?.error ? (
                      <div>
                        <p role="alert" className="rounded-lg bg-red-50 px-3 py-2.5 text-sm text-red-700">{sectionState[activeTab].error}</p>
                        <button
                          type="button"
                          onClick={() => handleSeedData(true)}
                          className="mt-3 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white"
                        >
                          Seed Existing Website Content
                        </button>
                      </div>
                    ) : (data[activeTab] || []).length === 0 ? (
                      <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center">
                        <p className="text-sm text-slate-500">No {activeTab} saved in Firestore yet.</p>
                        <button
                          type="button"
                          onClick={() => handleSeedData(true)}
                          className="mt-4 rounded-xl bg-primary px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-[#1b1449]"
                        >
                          Import Existing Website {activeTab}
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-4 max-h-[calc(100vh-20rem)] overflow-y-auto pr-1">
                        {(data[activeTab] || []).map((item) => {
                          const thumbSrc = item.imageUrl || null;
                          return (
                            <div key={item.id} className="flex gap-4 rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200/70">
                              {thumbSrc && (
                                <img src={thumbSrc} alt="" className="h-16 w-16 shrink-0 rounded-lg object-cover ring-1 ring-slate-200" />
                              )}
                              <div className="min-w-0 flex-1">
                                {activeTab === "programs" && (
                                  <>
                                    <div className="mb-1 flex items-center justify-between gap-3">
                                      <h3 className="font-semibold text-slate-900">{item.title}</h3>
                                      <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${
                                          item.programType === "completed"
                                            ? "bg-purple-100 text-purple-700"
                                            : "bg-blue-100 text-blue-700"
                                        }`}>
                                          {item.programType === "completed" ? "Completed Training" : "Upcoming"}
                                        </span>
                                        <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-700">{item.status}</span>
                                      </div>
                                    </div>
                                    {item.eventDate && (
                                      <p className="mb-1 text-[11px] font-medium text-slate-500">📅 Date: {item.eventDate}</p>
                                    )}
                                    <p className="text-xs text-slate-600 line-clamp-2">{item.description}</p>
                                    {Array.isArray(item.galleryImages) && item.galleryImages.length > 0 && (
                                      <p className="mt-1 text-[11px] font-medium text-primary">
                                        📸 {item.galleryImages.length} gallery {item.galleryImages.length === 1 ? "photo" : "photos"}
                                      </p>
                                    )}
                                  </>
                                )}

                                {activeTab === "instructors" && (
                                  <>
                                    <div className="mb-1 flex items-center justify-between gap-3">
                                      <h3 className="font-semibold text-slate-900">{item.name}</h3>
                                      <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-700">{item.availability}</span>
                                    </div>
                                    <p className="text-xs font-medium text-primary">{item.role}</p>
                                    {item.bio && <p className="mt-1 text-xs text-slate-600 line-clamp-2">{item.bio}</p>}
                                  </>
                                )}

                                {activeTab === "team" && (
                                  <>
                                    <div className="mb-1 flex items-center justify-between gap-3">
                                      <h3 className="font-semibold text-slate-900">{item.name}</h3>
                                      {item.order && <span className="text-[10px] font-bold text-slate-400">Order #{item.order}</span>}
                                    </div>
                                    <p className="text-xs font-medium text-primary">{item.role}</p>
                                    {item.bio && <p className="mt-1 text-xs text-slate-600 line-clamp-2">{item.bio}</p>}
                                  </>
                                )}

                                {activeTab === "news" && (
                                  <>
                                    <div className="mb-1 flex items-center justify-between gap-3">
                                      <h3 className="font-semibold text-slate-900">{item.title}</h3>
                                      {item.date && <span className="text-[10px] text-slate-500">{item.date}</span>}
                                    </div>
                                    <p className="text-xs text-slate-600 line-clamp-2">{item.summary}</p>
                                  </>
                                )}

                                {activeTab === "announcements" && (
                                  <>
                                    <h3 className="font-semibold text-slate-900">{item.title}</h3>
                                    <p className="mt-1 text-xs text-slate-600 whitespace-pre-line">{item.message}</p>
                                    <p className="mt-2 text-[11px] font-semibold text-primary">CTA: {item.ctaLabel} → {item.ctaUrl}</p>
                                  </>
                                )}

                                <div className="mt-3 flex gap-4 border-t border-slate-200/60 pt-2">
                                  <button onClick={() => handleEditEntry(activeTab, item)} className="text-xs font-semibold text-primary hover:underline">
                                    Edit
                                  </button>
                                  <button onClick={() => handleDelete(activeTab, item.id)} className="text-xs font-semibold text-red-600 hover:underline">
                                    Delete
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>

      {/* Reusable Custom Modal (Replaces native browser popups) */}
      <CustomConfirmModal
        isOpen={modalConfig.isOpen}
        title={modalConfig.title}
        message={modalConfig.message}
        confirmText={modalConfig.confirmText}
        cancelText={modalConfig.cancelText}
        type={modalConfig.type}
        onConfirm={modalConfig.onConfirm}
        onCancel={closeModal}
        showCancel={modalConfig.showCancel}
      />
    </div>
  );
};

export default AdminDashboard;
